// tiers.js — the board's three tiers, pure and tested. Owns the date
// helpers, heat, reasons, eligibility, and tier(). Watch.js renders.
//
//   NOW      exactly NOW_SLOTS when that many eligible cards exist: pinned
//            first (by heat), then OVERDUE|CHECK by heat, DUE|CHECKSOON by
//            heat, WAKE (a Holding card whose wake date has passed) by heat,
//            court Mine by heat. A fourth pinned card tops TODAY.
//   TODAY    the rest, reason priority then heat, capped at TODAY_CAP
//   BACKLOG  everything else eligible, plus parked cards
//
// Parked (app rule): court Holding with an effective wake date still ahead —
// a Holding card with no WakeDate wakes 30 days after LastTouched. A parked
// card never enters NOW or TODAY; once its wake passes it is eligible again.
// tier() takes the ALREADY-FILTERED list, so a court filter narrows all three
// tiers and NOW may then hold fewer than NOW_SLOTS.

import { isNewItem } from './counts';

export const NOW_SLOTS = 3;
export const TODAY_CAP = 5;
export const DUE_SOON_DAYS = 7;
export const HOLDING_DEFAULT_WAKE_DAYS = 30;
export const TOUCH_TERM_CAP = 15;
export const CHILD_NAMES = ['Taylor', 'Levi', 'John'];

const DAY_MS = 86400000;

// ── dates ──
export function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
export function daysBetween(a, b) { return Math.round((a - b) / DAY_MS); }
export function daysSinceTouched(item, today) {
  const t = parseDate(item.lastTouched);
  return t ? daysBetween(today, t) : null;
}
// Holding items with no explicit wakeDate default to a 30-day park from
// lastTouched — a parked item always carries a comeback date. Anchors are
// premises: no auto wake.
export function effectiveWakeDate(item) {
  const wake = parseDate(item.wakeDate);
  if (wake) return wake;
  if (item.kind === 'Anchor') return null;
  if (item.court === 'Holding') {
    const t = parseDate(item.lastTouched);
    if (t) return new Date(t.getFullYear(), t.getMonth(), t.getDate() + HOLDING_DEFAULT_WAKE_DAYS);
  }
  return null;
}
// Anchors never go cold. A parked Holding item is deliberate, not neglected —
// no cold until its effective wake passes.
export function isCold(item, today) {
  if (item.kind === 'Anchor') return false;
  if (item.court === 'Holding') {
    const wake = effectiveWakeDate(item);
    if (wake && wake > today) return false;
  }
  const d = daysSinceTouched(item, today);
  return d != null && Number(item.coldDays) > 0 && d > Number(item.coldDays);
}
export function isDone(item) { return item.status === 'Closed' || item.status === 'Killed'; }
export function isParked(item, today) {
  if (item.court !== 'Holding') return false;
  const wake = effectiveWakeDate(item);
  return !!wake && wake > today;
}
export function linksOf(item, type) { return (item.links || []).filter(l => l.type === type); }

// ── heat ──
// Walk the blocks chain breadth-first up to maxHops, collecting every
// dueDate along it. The seen-set guards against cycles.
function blockedChainDueDates(item, byId, maxHops) {
  const dues = [];
  const seen = new Set([item.id]);
  let frontier = linksOf(item, 'blocks').map(l => l.id).filter(id => byId.has(id));
  let hops = 0;
  while (frontier.length && hops < maxHops) {
    const next = [];
    for (const id of frontier) {
      if (seen.has(id)) continue;
      seen.add(id);
      const t = byId.get(id);
      const d = parseDate(t.dueDate);
      if (d) dues.push(d);
      linksOf(t, 'blocks').forEach(l => { if (byId.has(l.id) && !seen.has(l.id)) next.push(l.id); });
    }
    frontier = next;
    hops++;
  }
  return dues;
}

// The pre-D3 score: stakes, deadlines, blocking, re-asks, neglect,
// avoidance. heatOverride wins when set. Closed work carries no heat.
export function computeHeat(item, byId, items, today) {
  if (isDone(item)) return 0;
  if (item.heatOverride != null) return Math.max(0, Math.min(100, Number(item.heatOverride) || 0));
  let heat = 0;
  const chainDues = blockedChainDueDates(item, byId, 3);
  if (linksOf(item, 'blocks').some(l => byId.has(l.id))) {
    if (chainDues.length === 0) heat += 10;
    else heat += Math.min(...chainDues.map(d => daysBetween(d, today))) <= 60 ? 30 : 15;
  }
  const dueDates = [parseDate(item.dueDate), ...chainDues].filter(Boolean);
  if (dueDates.length) {
    const soonest = Math.min(...dueDates.map(d => daysBetween(d, today)));
    if (soonest <= 14) heat += 30;
    else if (soonest <= 30) heat += 20;
    else if (soonest <= 60) heat += 10;
    else if (soonest <= 90) heat += 5;
  }
  const reasks = (item.sources || []).filter(s => s.isReAsk).length;
  if (reasks > 0) heat += 15 + 5 * (reasks - 1);
  const dependents = items.filter(o => o.id !== item.id && (o.anchorId === item.id || o.wakeAfter === item.id)).length;
  let stakes = 0;
  if (item.insuredName) stakes = 15;
  if (item.domain === 'Family' && (item.kind === 'Anchor' || dependents >= 3)) stakes = Math.max(stakes, 15);
  if (item.domain === 'Family' && (CHILD_NAMES.includes(item.person) || item.person === 'Household')) stakes = Math.max(stakes, 10);
  heat += stakes;
  if (item.kind !== 'Anchor') {
    if (isCold(item, today)) heat += 10;
    else {
      const ds = daysSinceTouched(item, today);
      if (ds != null && Number(item.coldDays) > 0 && (Number(item.coldDays) + 1 - ds) <= 2) heat += 5;
    }
  }
  const parks = Number(item.parkCount) || 0;
  if (parks >= 3) heat += 20 * (parks - 2);
  if (dependents >= 3) heat += 10;
  return Math.min(100, heat);
}

const KIND_WEIGHT = { Decision: 10, Action: 5, Question: 5 };
const synopsisSaysMine = (item) => item.synopsis?.court === 'Mine' && Number(item.synopsis?.confidence) >= 0.75;

/**
 * D3 heat: computeHeat plus a court term (+20 when Court is Mine AND the
 * synopsis says Mine at ≥ 0.75; +10 when only one holds), a touch term
 * (1/day since LastTouched, cap 15), +10 when the synopsis check-back date
 * has passed, and a kind weight. Anchors keep their exemption; a
 * HeatOverride still wins outright.
 */
export function heat(item, byId, items, today) {
  const base = computeHeat(item, byId, items, today);
  if (isDone(item) || item.heatOverride != null || item.kind === 'Anchor') return base;
  let h = base;
  const mine = item.court === 'Mine';
  const syn = synopsisSaysMine(item);
  if (mine && syn) h += 20; else if (mine || syn) h += 10;
  const ds = daysSinceTouched(item, today);
  if (ds != null && ds > 0) h += Math.min(TOUCH_TERM_CAP, ds);
  const next = parseDate(item.synopsis?.nextCheck);
  if (next && next < today) h += 10;
  h += KIND_WEIGHT[item.kind] || 0;
  return Math.min(100, h);
}
export function heatTier(h) { return h >= 70 ? 'hot' : h >= 50 ? 'warm' : h >= 30 ? 'faint' : 'dim'; }

// ── reasons ──
export const REASON_PRIORITY = { OVERDUE: 5, CHECK: 5, DUE: 4, CHECKSOON: 4, WAKE: 3, ANCHOR: 2, COLD: 1 };
export const REASON_COLORS = { OVERDUE: '#ff4444', CHECK: '#ff4444', DUE: '#e6a817', CHECKSOON: '#e6a817', WAKE: '#44cc44', ANCHOR: '#8a9aa8', COLD: '#ff4444', PINNED: '#1f4e9e', LISTED: '#1f4e9e', MINE: '#4a3a05' };

/** Why a card is live, highest priority first. Anchors: ANCHOR within 30 days and nothing else. */
export function reasons(item, today) {
  const out = [];
  const due = parseDate(item.dueDate);
  if (item.kind === 'Anchor') {
    if (due && daysBetween(due, today) <= 30) out.push('ANCHOR');
    return out;
  }
  const next = parseDate(item.synopsis?.nextCheck);
  if (due && due < today) out.push('OVERDUE');
  if (next && next < today) out.push('CHECK');
  if (due && due >= today && daysBetween(due, today) <= DUE_SOON_DAYS) out.push('DUE');
  if (next && next >= today && daysBetween(next, today) <= DUE_SOON_DAYS) out.push('CHECKSOON');
  const wake = effectiveWakeDate(item);
  if (wake && wake <= today) out.push('WAKE');
  if (isCold(item, today)) out.push('COLD');
  return out.sort((a, b) => REASON_PRIORITY[b] - REASON_PRIORITY[a]);
}
export const todayReasons = reasons;
const topPriority = (rs) => (rs.length ? REASON_PRIORITY[rs[0]] : 0);

/** Open, not on the NEW rail, not parked. */
export function eligible(item, today) {
  return item?.status === 'Open' && !isNewItem(item) && !isParked(item, today);
}

/**
 * @param items    the already-filtered board items
 * @param heatMap  id -> heat
 * @returns {{ now: entry[], today: entry[], backlog: item[], todayTotal: number, parked: item[] }}
 *   entry = { item, heat, reasons, reason, fill, pinned }
 *   fill  = 'PINNED' | 'LISTED' | 'OVERDUE' | 'CHECK' | 'DUE' | 'CHECKSOON' | 'WAKE' | 'MINE' | ''   (how the slot was filled)
 */
export function tier(items, { heatMap = {}, today = new Date() } = {}) {
  const h = (i) => Number(heatMap[i.id]) || 0;
  const byHeat = (a, b) => h(b) - h(a) || a.id - b.id;
  const open = (items || []).filter(i => i?.status === 'Open' && !isNewItem(i));
  const parked = open.filter(i => isParked(i, today));
  const pool = open.filter(i => !isParked(i, today));
  const entry = (item, fill = '') => {
    const rs = reasons(item, today);
    return { item, heat: h(item), reasons: rs, reason: rs[0] || fill, fill, pinned: item.pinned === true };
  };
  const taken = new Set();
  const now = [];
  const pinned = pool.filter(i => i.pinned === true).sort(byHeat);
  for (const i of pinned) { if (now.length < NOW_SLOTS) { now.push(entry(i, 'PINNED')); taken.add(i.id); } }
  const extraPinned = pinned.filter(i => !taken.has(i.id));
  for (const i of extraPinned) taken.add(i.id);
  // Marc's List: a card one of his open tasks points at (item.listed, set by
  // Watch.js from My_List) ranks right after pins and ahead of the computed
  // reasons — his list outranks the heat score, a pin still wins.
  const listed = pool.filter(i => i.listed === true && !taken.has(i.id)).sort(byHeat);
  for (const i of listed) { if (now.length < NOW_SLOTS) { now.push(entry(i, 'LISTED')); taken.add(i.id); } }
  const extraListed = listed.filter(i => !taken.has(i.id));
  for (const i of extraListed) taken.add(i.id);
  const fills = [
    ['OVERDUE', (i, rs) => rs.includes('OVERDUE') || rs.includes('CHECK')],
    ['DUE', (i, rs) => rs.includes('DUE') || rs.includes('CHECKSOON')],
    ['WAKE', (i, rs) => rs.includes('WAKE')],
    ['MINE', (i) => i.court === 'Mine'],
  ];
  for (const [label, test] of fills) {
    if (now.length >= NOW_SLOTS) break;
    const cands = pool.filter(i => !taken.has(i.id) && test(i, reasons(i, today))).sort(byHeat);
    for (const i of cands) {
      if (now.length >= NOW_SLOTS) break;
      const rs = reasons(i, today);
      const fill = label === 'OVERDUE' ? (rs.includes('OVERDUE') ? 'OVERDUE' : 'CHECK')
        : label === 'DUE' ? (rs.includes('DUE') ? 'DUE' : 'CHECKSOON') : label;
      now.push(entry(i, fill)); taken.add(i.id);
    }
  }
  const rest = pool.filter(i => !taken.has(i.id)).map(i => entry(i))
    .sort((a, b) => topPriority(b.reasons) - topPriority(a.reasons) || b.heat - a.heat || a.item.id - b.item.id);
  const todayAll = [...extraPinned.map(i => entry(i, 'PINNED')), ...extraListed.map(i => entry(i, 'LISTED')), ...rest];
  const todayList = todayAll.slice(0, TODAY_CAP);
  const backlog = [...todayAll.slice(TODAY_CAP).map(e => e.item), ...parked];
  return { now, today: todayList, backlog, todayTotal: todayAll.length, parked };
}
